import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Shield, ArrowRight } from "@phosphor-icons/react";
import LegalPage from "./LegalPage";

const STORAGE_KEY = "br_privacy_optout";

/** Shared CPRA opt-out screen. Two variants: "sale-share" and "limit-sensitive".
 *  Stores the choice locally (and is intended to be POSTed to a future
 *  /api/privacy/opt-out endpoint — tracked in PRD backlog). GPC honored. */
export default function OptOutForm({
  variant,
  title,
  eyebrow,
  intro,
  testid,
  successLine,
}) {
  const [recorded, setRecorded] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const gpc = typeof navigator !== "undefined" && navigator.globalPrivacyControl === true;

  // Pre-fill recorded state from local storage / GPC.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const state = raw ? JSON.parse(raw) : {};
      if (state[variant]) setRecorded(true);
    } catch {}
  }, [variant]);

  // GPC users — auto-record on mount, no click needed.
  useEffect(() => {
    if (!gpc) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const state = raw ? JSON.parse(raw) : {};
      if (!state[variant]) {
        state[variant] = { method: "GPC", at: new Date().toISOString() };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      }
      setRecorded(true);
    } catch {}
  }, [gpc, variant]);

  const submit = (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const state = raw ? JSON.parse(raw) : {};
      state[variant] = { method: "manual", email: email || null, at: new Date().toISOString() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      setRecorded(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <LegalPage
      testid={testid}
      eyebrow={eyebrow}
      title={title}
      lastUpdated="14 May 2026 · Version 1.0"
      intro={intro}
    >
      <div
        className={`my-8 rounded-3xl border-2 p-6 lg:p-8 ${
          recorded ? "border-mint bg-mint-soft/40" : "border-cobalt-100 bg-cobalt-50/30"
        }`}
        data-testid={`${testid}-card`}
      >
        {recorded ? (
          <div className="flex items-start gap-4" data-testid={`${testid}-recorded`}>
            <CheckCircle size={28} weight="fill" className="text-mint flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="!mt-0 !mb-2">Your request has been recorded</h3>
              <p>{successLine}</p>
              {gpc && (
                <p className="text-sm text-ink-700">
                  We also detected the <strong>Global Privacy Control (GPC)</strong> signal in your
                  browser and treat it as a valid opt-out for California, Colorado, Connecticut,
                  Texas and Oregon residents.
                </p>
              )}
              <p className="text-sm text-ink-700">
                If you’d like a written confirmation, email{" "}
                <a href="mailto:hello@blueroam.io">hello@blueroam.io</a> from your account address.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid={`${testid}-form`}>
            <div className="flex items-start gap-3">
              <Shield size={26} weight="bold" className="text-cobalt-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="!mt-0 !mb-1">Submit your request</h3>
                <p className="!my-1 text-sm">
                  No account required. Adding your email is optional and only used to send you a
                  confirmation receipt.
                </p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5" htmlFor="optout-email">
                Email (optional)
              </label>
              <input
                id="optout-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white outline-none focus:border-cobalt-500"
                data-testid={`${testid}-email`}
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="btn-primary text-base group"
              data-testid={`${testid}-submit`}
            >
              {busy ? "Recording…" : "Confirm my opt-out"}
              <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
            </button>
          </form>
        )}
      </div>

      <h2>What this means</h2>
      <p>
        BlueRoam does not sell personal information for money. Under the California Consumer Privacy
        Act of 2018 (as amended by the CPRA) and equivalent laws in Virginia, Colorado, Connecticut,
        Texas, Oregon and Utah, “sale” and “share” have technical meanings that include disclosing
        identifiers, internet activity and approximate geolocation to advertising partners for
        cross-context behavioural advertising.
      </p>
      <p>
        By submitting this request, you are telling BlueRoam to stop “selling” or “sharing” your
        personal information for those purposes going forward, and (where applicable) to limit the
        use of your sensitive personal information. We action requests <strong>within 15 days</strong>{" "}
        and you can re-opt-in at any time by clearing the request from your browser or contacting
        us.
      </p>

      <h2>Have an account with us?</h2>
      <p>
        Signed-in users can also exercise this and other CPRA rights from their account settings,
        via our <Link to="/legal/privacy-request">privacy request webform</Link>, or by emailing{" "}
        <a href="mailto:hello@blueroam.io">hello@blueroam.io</a>. See our{" "}
        <Link to="/legal/us-privacy">US Privacy Notice</Link> for the full list of rights by state.
      </p>

      <h2>Authorised agents</h2>
      <p>
        Agents acting on a consumer’s behalf must provide written authorisation and verify both the
        agent and the consumer — see §5.3 of the <Link to="/legal/us-privacy">US Privacy Notice</Link>.
      </p>
    </LegalPage>
  );
}
